import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, UserCog, Building2, ArrowRight, Search, RotateCcw, ChevronDown,
  CheckCircle2, AlertCircle, Loader2, Layers, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PurgeCancelledButton from '@/components/appointments/PurgeCancelledButton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StudioMigrationPage = () => {
  const [studios, setStudios] = useState([]);
  const [activeTab, setActiveTab] = useState('customers');
  // shared
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // customers
  const [customers, setCustomers] = useState([]);
  const [custStudio, setCustStudio] = useState('all');
  const [custSearch, setCustSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [custLoading, setCustLoading] = useState(false);
  // Einzel-Kunde für "Stornierte Termine bereinigen"
  const [purgeCustSearch, setPurgeCustSearch] = useState('');
  const [purgeCust, setPurgeCust] = useState(null);

  // users
  const [users, setUsers] = useState([]);
  const [userStudio, setUserStudio] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userLoading, setUserLoading] = useState(false);

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  // FLEX-Entkopplung (Datenpflege)
  const [flexPreview, setFlexPreview] = useState(null);
  const [flexStatus, setFlexStatus] = useState(null);
  const [flexBusy, setFlexBusy] = useState(false);
  const [flexConfirmOpen, setFlexConfirmOpen] = useState(false);

  const loadFlexStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/flex-decouple/status`, auth());
      setFlexStatus(r.data);
    } catch { /* optional */ }
  };
  const runFlexPreview = async () => {
    setFlexBusy(true);
    try {
      const r = await axios.get(`${API}/admin/flex-decouple/preview`, auth());
      setFlexPreview(r.data);
    } catch { toast.error('Vorschau fehlgeschlagen'); }
    finally { setFlexBusy(false); }
  };
  const startFlexExecute = async (force = false) => {
    setFlexConfirmOpen(false);
    try {
      await axios.post(`${API}/admin/flex-decouple/execute${force ? '?force=true' : ''}`, {}, auth());
      toast.success(force ? 'Migration neu gestartet' : 'Migration gestartet');
      setFlexStatus({ state: 'running', phase: 'split', processed: 0, total: 0 });
      loadFlexStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Start fehlgeschlagen');
    }
  };

  useEffect(() => { loadFlexStatus(); }, []);
  useEffect(() => {
    if (flexStatus?.state !== 'running') return undefined;
    const t = setInterval(loadFlexStatus, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexStatus?.state]);

  // ---- initial load ----
  useEffect(() => {
    axios.get(`${API}/studios/all`, auth())
      .then(r => setStudios(r.data || []))
      .catch(() => toast.error('Studios konnten nicht geladen werden'));
  }, []);

  useEffect(() => {
    if (activeTab === 'customers') loadCustomers();
    if (activeTab === 'users') loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, custStudio, userStudio]);

  const loadCustomers = async () => {
    setCustLoading(true);
    try {
      const params = {};
      if (custStudio !== 'all') params.studio_id = custStudio;
      if (custSearch) params.search = custSearch;
      const res = await axios.get(`${API}/admin/bulk/customers-overview`, { ...auth(), params });
      setCustomers(res.data || []);
      setSelectedCustomers(new Set());
    } catch (e) {
      toast.error('Kunden konnten nicht geladen werden');
    } finally {
      setCustLoading(false);
    }
  };

  const loadUsers = async () => {
    setUserLoading(true);
    try {
      const params = {};
      if (userStudio !== 'all') params.studio_id = userStudio;
      if (userSearch) params.search = userSearch;
      const res = await axios.get(`${API}/admin/bulk/users-overview`, { ...auth(), params });
      setUsers(res.data || []);
      setSelectedUsers(new Set());
    } catch (e) {
      toast.error('Trainer konnten nicht geladen werden');
    } finally {
      setUserLoading(false);
    }
  };

  const studioName = (id) => studios.find(s => s.id === id)?.short_name || studios.find(s => s.id === id)?.name || '—';
  const studioById = (id) => studios.find(s => s.id === id);

  // ---- selection helpers ----
  const toggleCust = (id) => {
    const s = new Set(selectedCustomers);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedCustomers(s);
  };
  const toggleUser = (id) => {
    const s = new Set(selectedUsers);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedUsers(s);
  };
  const toggleAllCust = () => {
    if (selectedCustomers.size === customers.length) setSelectedCustomers(new Set());
    else setSelectedCustomers(new Set(customers.map(c => c.id)));
  };
  const toggleAllUsers = () => {
    if (selectedUsers.size === users.length) setSelectedUsers(new Set());
    else setSelectedUsers(new Set(users.map(u => u.id)));
  };

  // ---- bulk actions ----
  const askMoveCustomers = (targetId, mode) => {
    if (selectedCustomers.size === 0) return toast.error('Wähle mindestens 1 Kunden');
    setPendingMove({
      kind: 'customers',
      targetId,
      mode,
      ids: Array.from(selectedCustomers),
      update_future_appointments: mode === 'replace' || mode === 'set_primary',
    });
    setConfirmOpen(true);
  };

  const askMoveUsers = (targetId, mode) => {
    if (selectedUsers.size === 0) return toast.error('Wähle mindestens 1 Trainer');
    setPendingMove({
      kind: 'users',
      targetId,
      mode,
      ids: Array.from(selectedUsers),
    });
    setConfirmOpen(true);
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    setMoveSubmitting(true);
    try {
      if (pendingMove.kind === 'customers') {
        const res = await axios.post(
          `${API}/admin/bulk/customers/move-studio`,
          {
            customer_ids: pendingMove.ids,
            target_studio_id: pendingMove.targetId,
            mode: pendingMove.mode,
            update_future_appointments: pendingMove.update_future_appointments,
          },
          auth(),
        );
        toast.success(`${res.data.moved_customers} Kunden (${pendingMove.mode}) + ${res.data.moved_appointments} Termine → ${res.data.target_studio}`);
        loadCustomers();
      } else {
        const res = await axios.post(
          `${API}/admin/bulk/users/move-studio`,
          {
            user_ids: pendingMove.ids,
            studio_id: pendingMove.targetId,
            mode: pendingMove.mode,
          },
          auth(),
        );
        toast.success(`${res.data.modified_users} Trainer (${pendingMove.mode}) → ${res.data.studio}`);
        loadUsers();
      }
      setConfirmOpen(false);
      setPendingMove(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Verschieben');
    } finally {
      setMoveSubmitting(false);
    }
  };

  // ---- search debouncer ----
  useEffect(() => {
    const t = setTimeout(() => {
      if (activeTab === 'customers') loadCustomers();
      if (activeTab === 'users') loadUsers();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custSearch, userSearch]);

  // ---- name helper for customer (mixed fields in legacy data) ----
  const custName = (c) => {
    const a = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
    if (a) return a;
    if (c.name || c.display_name || c.full_name) return c.name || c.display_name || c.full_name;
    if (c.email) return c.email;
    return `Kunde ${(c.id || '').slice(0, 8)}…`;
  };
  const custHasRealName = (c) => !!(c.first_name || c.last_name || c.name || c.display_name || c.full_name || c.email);

  // Sortiere benannte Kunden vor unbenannten Dummies
  const sortedCustomers = useMemo(() => {
    return [...customers].sort((a, b) => {
      const aH = custHasRealName(a) ? 0 : 1;
      const bH = custHasRealName(b) ? 0 : 1;
      if (aH !== bH) return aH - bH;
      return custName(a).localeCompare(custName(b));
    });
  }, [customers]);

  const targetStudios = useMemo(() => studios.filter(s => s.is_active !== false), [studios]);

  return (
    <div className="p-4 md:p-8 max-w-6xl space-y-6" data-testid="studio-migration-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ArrowRight className="h-6 w-6 text-teal-600" />
            Studio-Migration (Bulk)
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Kunden und Trainer zwischen Studios verschieben. Nützlich beim Onboarding eines neuen Standorts.
          </p>
        </div>
        {/* Iter 287: Repair-Button — ergänzt fehlende studio_ids bei
            Bestandskunden (z.B. nach OTP-Login Migration). */}
        <Button
          variant="outline"
          onClick={async () => {
            if (!window.confirm('Alle Kunden ohne Multi-Studio-Zuordnung jetzt reparieren?')) return;
            try {
              const res = await axios.post(`${API}/customers/repair-studios`, {}, auth());
              toast.success(`${res.data.repaired} von ${res.data.total_checked} Kunden repariert`);
            } catch (e) {
              toast.error('Reparatur fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
            }
          }}
          data-testid="repair-studios-btn"
          className="gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Studio-Zuordnung reparieren
        </Button>
      </div>

      {/* FLEX-Entkopplung (Datenpflege) */}
      <Card className="border-indigo-200" data-testid="flex-decouple-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            FLEX-Termine entkoppeln (Datenpflege)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-gray-600">
            Alte Gruppen-Trainings (1:2/1:3/1:4) werden in eigenständige Einzel-Termine pro Teilnehmer aufgeteilt,
            damit jeder Teilnehmer im Kalender unter seinem Namen sichtbar ist. Idempotent (mehrfach ausführbar),
            mit automatischer Sicherung. Mindbody bleibt unberührt – es entstehen KEINE neuen Mindbody-Termine.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={runFlexPreview}
              disabled={flexBusy || flexStatus?.state === 'running'}
              data-testid="flex-preview-btn"
            >
              {flexBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Vorschau (Dry-Run)
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => setFlexConfirmOpen(true)}
              disabled={flexStatus?.state === 'running'}
              data-testid="flex-execute-btn"
            >
              {flexStatus?.state === 'running'
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Läuft…</>
                : 'Jetzt ausführen'}
            </Button>
          </div>
          {flexPreview && (
            <div className="bg-slate-50 border rounded p-3 text-xs" data-testid="flex-preview-result">
              Vorschau: <strong>{flexPreview.source_documents}</strong> Sammel-Termine → <strong>{flexPreview.participant_documents}</strong> Einzel-Termine
              ({flexPreview.new_documents} neu). {flexPreview.legacy_single_documents} Alt-Dokumente werden bereinigt.
              {flexPreview.recovered_participant_documents > 0 && (
                <div className="mt-1 text-emerald-700 font-medium" data-testid="flex-preview-recover">
                  ♻️ {flexPreview.recovered_participant_documents} zuvor verlorene Teilnehmer werden aus dem Backup wiederhergestellt.
                </div>
              )}
            </div>
          )}
          {flexStatus?.state === 'running' && (
            <div className="text-xs text-indigo-700 space-y-2" data-testid="flex-progress">
              {flexStatus.phase === 'cleanup' ? (
                <div>🧹 Alt-Datensätze aufräumen: {flexStatus.processed || 0}/{flexStatus.total || 0}…</div>
              ) : (
                <div>✂️ Termine aufteilen: {flexStatus.processed || 0}/{flexStatus.total || 0} Sammel-Termine…</div>
              )}
              <div className="text-[11px] text-gray-500">
                Läuft im Hintergrund – du kannst die Seite verlassen. Nach dem Aufteilen folgt eine kurze Aufräum-Phase.
                Falls der Fortschritt sehr lange stillsteht (z. B. Server-Neustart), kannst du sicher neu starten – die Migration ist idempotent.
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                onClick={() => startFlexExecute(true)}
                data-testid="flex-force-restart-btn"
              >
                Neu starten (falls hängengeblieben)
              </Button>
            </div>
          )}
          {flexStatus?.state === 'done' && flexStatus.summary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800" data-testid="flex-done">
              ✅ Fertig: {flexStatus.summary.participant_documents} Einzel-Termine erzeugt · {flexStatus.summary.remaining_coupled_documents} verbleibende gekoppelte.
              {flexStatus.summary.recovered_participant_documents > 0
                ? ` ♻️ ${flexStatus.summary.recovered_participant_documents} verlorene Teilnehmer aus Backup wiederhergestellt.`
                : ''}
              {flexStatus.studio_integrity
                ? (flexStatus.studio_integrity.ok
                    ? ' Studio-Integrität ok.'
                    : ` ⚠️ ${flexStatus.studio_integrity.orphaned_studio_ids} verwaiste Studio-Referenz(en) gefunden.`)
                : ''}
            </div>
          )}
          {flexStatus?.state === 'error' && (
            <div className="bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-700" data-testid="flex-error">
              Fehler: {flexStatus.error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stornierte/No-Show-Termine bereinigen (global) */}
      <Card className="border-rose-200" data-testid="purge-cancelled-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-600" />
            Stornierte Termine bereinigen (Datenpflege)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-gray-600">
            Entfernt alle <strong>stornierten</strong> und <strong>No-Show</strong>-Termine ALLER Kunden
            endgültig aus der Datenbank – auch vergangene. Nützlich, wenn durch mehrfach stornierte Serien
            viele „Leichen" im Kalender/Profil zurückgeblieben sind. Aktive/geplante Termine bleiben unberührt.
          </p>
          <PurgeCancelledButton
            label="Stornierte Termine aller Kunden entfernen"
            onDone={() => { /* rein serverseitig, keine Liste hier */ }}
            data-testid="purge-cancelled-global-btn"
          />

          <div className="pt-3 border-t space-y-2">
            <p className="text-gray-600">Oder nur für <strong>einen einzelnen Kunden</strong>:</p>
            {purgeCust ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="purge-selected-row">
                <Badge variant="outline" className="text-xs" data-testid="purge-selected-customer">
                  {custName(purgeCust)}{purgeCust.email ? ` · ${purgeCust.email}` : ''}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => { setPurgeCust(null); setPurgeCustSearch(''); }}
                  data-testid="purge-clear-customer"
                >
                  Ändern
                </Button>
                <PurgeCancelledButton
                  customerId={purgeCust.id}
                  label={`Stornierte von ${custName(purgeCust)} entfernen`}
                  variant="outline"
                  className="border-rose-300 text-rose-700"
                  data-testid="purge-cancelled-single-btn"
                />
              </div>
            ) : (
              <div className="relative max-w-md" data-testid="purge-customer-picker">
                <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
                <Input
                  placeholder="Kunde suchen (Name / E-Mail)…"
                  value={purgeCustSearch}
                  onChange={(e) => setPurgeCustSearch(e.target.value)}
                  className="pl-8"
                  data-testid="purge-customer-search"
                />
                {purgeCustSearch.trim() && (() => {
                  const q = purgeCustSearch.trim().toLowerCase();
                  const matches = customers.filter((c) =>
                    `${c.first_name || ''} ${c.last_name || ''} ${c.name || ''} ${c.email || ''}`.toLowerCase().includes(q)
                  );
                  return (
                    <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {matches.slice(0, 10).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => { setPurgeCust(c); setPurgeCustSearch(''); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                          data-testid={`purge-customer-option-${c.id}`}
                        >
                          <span className="font-medium">{custName(c)}</span>
                          {c.email ? <span className="text-gray-500"> · {c.email}</span> : null}
                        </button>
                      ))}
                      {matches.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Kein Kunde gefunden</div>
                      )}
                    </div>
                  );
                })()}
                {customers.length === 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">Kundenliste lädt… (öffne ggf. kurz den Tab „Kunden" unten)</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="migration-tab-customers">
            <Users className="h-4 w-4 mr-2" />
            Kunden
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="migration-tab-users">
            <UserCog className="h-4 w-4 mr-2" />
            Trainer / Admins
          </TabsTrigger>
        </TabsList>

        {/* ============ CUSTOMERS ============ */}
        <TabsContent value="customers">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Kunden filtern & verschieben</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
                    <Input
                      placeholder="Name / E-Mail..."
                      value={custSearch}
                      onChange={(e) => setCustSearch(e.target.value)}
                      className="pl-8 w-[200px]"
                      data-testid="customer-search"
                    />
                  </div>
                  <Select value={custStudio} onValueChange={setCustStudio}>
                    <SelectTrigger className="w-[180px]" data-testid="customer-studio-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Studios</SelectItem>
                      <SelectItem value="_none_">⚠️ Kein Studio</SelectItem>
                      {studios.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadCustomers} data-testid="customer-refresh">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bulk action bar */}
              {selectedCustomers.size > 0 && (
                <div className="mb-3 p-3 rounded-lg border border-teal-200 bg-teal-50 space-y-2" data-testid="customer-bulk-bar">
                  <div className="flex items-center gap-2 text-sm text-teal-900">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-semibold">{selectedCustomers.size}</span> ausgewählt
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <Select onValueChange={(sid) => askMoveCustomers(sid, 'add')}>
                      <SelectTrigger className="w-[200px]" data-testid="customer-add-target">
                        <SelectValue placeholder="🟢 Studio HINZUFÜGEN" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>+ {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(sid) => askMoveCustomers(sid, 'remove')}>
                      <SelectTrigger className="w-[200px]" data-testid="customer-remove-target">
                        <SelectValue placeholder="🔴 Studio ENTFERNEN" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>− {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(sid) => askMoveCustomers(sid, 'set_primary')}>
                      <SelectTrigger className="w-[210px]" data-testid="customer-primary-target">
                        <SelectValue placeholder="⭐ Stamm-Studio setzen" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>⭐ {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(sid) => askMoveCustomers(sid, 'replace')}>
                      <SelectTrigger className="w-[210px]" data-testid="customer-replace-target">
                        <SelectValue placeholder="⚠️ Studios ERSETZEN durch..." />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>nur {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {custLoading ? (
                <div className="py-10 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> lädt...
                </div>
              ) : customers.length === 0 ? (
                <div className="py-10 text-center text-gray-500 text-sm">Keine Kunden mit aktuellem Filter</div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="flex items-center gap-3 p-2.5 bg-slate-50 border-b text-xs font-semibold text-gray-600">
                    <Checkbox
                      checked={customers.length > 0 && selectedCustomers.size === customers.length}
                      onCheckedChange={toggleAllCust}
                      data-testid="customer-select-all"
                    />
                    <div className="flex-1">Name</div>
                    <div className="w-56 text-xs">E-Mail</div>
                    <div className="flex-1 text-xs">Studios (⭐ Stamm)</div>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    {sortedCustomers.map(c => {
                      const sids = [c.studio_id, ...((c.studio_ids || []).filter(s => s !== c.studio_id))].filter(Boolean);
                      return (
                      <div
                        key={c.id}
                        className={`flex items-center gap-3 p-2 border-b last:border-b-0 hover:bg-slate-50 ${selectedCustomers.has(c.id) ? 'bg-teal-50/50' : ''} ${!custHasRealName(c) ? 'opacity-60' : ''}`}
                        data-testid={`customer-row-${c.id}`}
                      >
                        <Checkbox
                          checked={selectedCustomers.has(c.id)}
                          onCheckedChange={() => toggleCust(c.id)}
                        />
                        <div className="flex-1 text-sm truncate">{custName(c)}</div>
                        <div className="w-56 text-xs text-gray-500 truncate">{c.email || '—'}</div>
                        <div className="flex-1 flex flex-wrap gap-1">
                          {sids.length === 0 ? (
                            <Badge className="text-[10px] bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Kein Studio
                            </Badge>
                          ) : sids.map((sid, idx) => (
                            <Badge
                              key={sid}
                              variant="outline"
                              className={`text-[10px] ${sid === c.studio_id ? 'border-teal-400 bg-teal-50 text-teal-700' : ''}`}
                            >
                              {sid === c.studio_id && '⭐ '}
                              <Building2 className="h-3 w-3 mr-1" />
                              {studioName(sid)}
                            </Badge>
                          ))}
                        </div>
                        <PurgeCancelledButton
                          customerId={c.id}
                          customerName={custName(c)}
                          iconOnly
                          variant="ghost"
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                          data-testid={`purge-cancelled-row-${c.id}`}
                          onDone={() => loadCustomers()}
                        />
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-2 text-[11px] text-gray-500 italic">
                💡 <strong>Option B Multi-Studio</strong>: Kunden können MEHREREN Studios angehören. Das <strong>Stamm-Studio</strong> (⭐) bestimmt wo Abos & Rechnungs-Adresse hängen. <strong>Train-and-Pay-Termine</strong> fliessen automatisch ins Studio wo der Kunde tatsächlich trainiert. „HINZUFÜGEN" erweitert die Studio-Liste, „ENTFERNEN" entfernt eines, „Stamm-Studio setzen" ändert das Hauptstudio.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ USERS / TRAINERS ============ */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Trainer & Admins zuweisen</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
                    <Input
                      placeholder="Name / E-Mail..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-8 w-[200px]"
                      data-testid="user-search"
                    />
                  </div>
                  <Select value={userStudio} onValueChange={setUserStudio}>
                    <SelectTrigger className="w-[180px]" data-testid="user-studio-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Studios</SelectItem>
                      <SelectItem value="_none_">⚠️ Kein Studio</SelectItem>
                      {studios.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadUsers} data-testid="user-refresh">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedUsers.size > 0 && (
                <div className="mb-3 p-3 rounded-lg border border-teal-200 bg-teal-50 space-y-2" data-testid="user-bulk-bar">
                  <div className="flex items-center gap-2 text-sm text-teal-900">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-semibold">{selectedUsers.size}</span> ausgewählt
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <Select onValueChange={(sid) => askMoveUsers(sid, 'add')}>
                      <SelectTrigger className="w-[200px]" data-testid="user-add-target">
                        <SelectValue placeholder="🟢 Studio HINZUFÜGEN" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>+ {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(sid) => askMoveUsers(sid, 'remove')}>
                      <SelectTrigger className="w-[200px]" data-testid="user-remove-target">
                        <SelectValue placeholder="🔴 Studio ENTFERNEN" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>− {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(sid) => askMoveUsers(sid, 'replace')}>
                      <SelectTrigger className="w-[210px]" data-testid="user-replace-target">
                        <SelectValue placeholder="⚠️ Studios ERSETZEN durch..." />
                      </SelectTrigger>
                      <SelectContent>
                        {targetStudios.map(s => (<SelectItem key={s.id} value={s.id}>nur {s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {userLoading ? (
                <div className="py-10 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> lädt...
                </div>
              ) : users.length === 0 ? (
                <div className="py-10 text-center text-gray-500 text-sm">Keine Trainer mit aktuellem Filter</div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="flex items-center gap-3 p-2.5 bg-slate-50 border-b text-xs font-semibold text-gray-600">
                    <Checkbox
                      checked={users.length > 0 && selectedUsers.size === users.length}
                      onCheckedChange={toggleAllUsers}
                      data-testid="user-select-all"
                    />
                    <div className="flex-1">Name</div>
                    <div className="w-20 text-xs">Rolle</div>
                    <div className="w-56 text-xs">E-Mail</div>
                    <div className="flex-1 text-xs">Zugewiesene Studios</div>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    {users.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-3 p-2 border-b last:border-b-0 hover:bg-slate-50 ${selectedUsers.has(u.id) ? 'bg-teal-50/50' : ''}`}
                        data-testid={`user-row-${u.id}`}
                      >
                        <Checkbox
                          checked={selectedUsers.has(u.id)}
                          onCheckedChange={() => toggleUser(u.id)}
                        />
                        <div className="flex-1 text-sm truncate">
                          {[u.first_name, u.last_name].filter(Boolean).join(' ') || '(unbenannt)'}
                        </div>
                        <div className="w-20">
                          <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                        </div>
                        <div className="w-56 text-xs text-gray-500 truncate">{u.email || '—'}</div>
                        <div className="flex-1 flex flex-wrap gap-1">
                          {(u.studio_ids || []).length === 0 ? (
                            <Badge className="text-[10px] bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">
                              Kein Studio
                            </Badge>
                          ) : (
                            (u.studio_ids || []).map(sid => {
                              const s = studioById(sid);
                              return (
                                <Badge key={sid} variant="outline" className="text-[10px]">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  {s?.short_name || s?.name || sid.slice(0, 8)}
                                </Badge>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2 text-[11px] text-gray-500 italic">
                💡 Trainer können <strong>mehreren Studios</strong> zugewiesen sein. „HINZUFÜGEN" erweitert die Liste, „ENTFERNEN" nimmt eines weg, „ERSETZEN" überschreibt alles mit dem gewählten Studio.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ CONFIRM DIALOG ============ */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk-Migration bestätigen</DialogTitle>
          </DialogHeader>
          {pendingMove && (
            <div className="py-3 space-y-3 text-sm">
              {pendingMove.kind === 'customers' ? (
                <>
                  <p>
                    <span className="font-semibold">{pendingMove.ids.length}</span> Kunde(n):{' '}
                    {pendingMove.mode === 'add' && <>Studio <span className="font-semibold text-emerald-700">{studioName(pendingMove.targetId)}</span> wird zur Studio-Liste hinzugefügt (Stamm-Studio bleibt unverändert).</>}
                    {pendingMove.mode === 'remove' && <>Studio <span className="font-semibold text-rose-700">{studioName(pendingMove.targetId)}</span> wird aus der Studio-Liste entfernt.</>}
                    {pendingMove.mode === 'set_primary' && <>Stamm-Studio (für Abos & Rechnungs-Adresse) wird auf <span className="font-semibold text-teal-700">{studioName(pendingMove.targetId)}</span> gesetzt.</>}
                    {pendingMove.mode === 'replace' && <>Studio-Liste wird komplett durch <span className="font-semibold text-amber-700">[{studioName(pendingMove.targetId)}]</span> ersetzt (auch Stamm-Studio).</>}
                  </p>
                  {(pendingMove.mode === 'replace' || pendingMove.mode === 'set_primary') && (
                    <p className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                      ⚡ Zukünftige Termine OHNE Studio-Zuordnung werden auf das neue Stamm-Studio umgehängt. Termine die explizit auf ein anderes Trainings-Studio gebucht wurden bleiben unangetastet (Train-and-Pay-Logik).
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    <span className="font-semibold">{pendingMove.ids.length}</span> Trainer:{' '}
                    {pendingMove.mode === 'add' && <>Studio <span className="font-semibold text-emerald-700">{studioName(pendingMove.targetId)}</span> wird hinzugefügt.</>}
                    {pendingMove.mode === 'remove' && <>Studio <span className="font-semibold text-rose-700">{studioName(pendingMove.targetId)}</span> wird entfernt.</>}
                    {pendingMove.mode === 'replace' && <>Studio-Liste wird durch <span className="font-semibold text-amber-700">[{studioName(pendingMove.targetId)}]</span> ersetzt.</>}
                  </p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={moveSubmitting}>Abbrechen</Button>
            <Button
              onClick={confirmMove}
              disabled={moveSubmitting}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-move-submit"
            >
              {moveSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verschiebt...</> : 'Jetzt verschieben'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FLEX-Ausführung bestätigen */}
      <Dialog open={flexConfirmOpen} onOpenChange={setFlexConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>FLEX-Entkopplung ausführen?</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm text-gray-700">
            <p>
              Alle gekoppelten Gruppen-Trainings werden in Einzel-Termine pro Teilnehmer aufgeteilt.
              Der Vorgang ist idempotent und sichert jedes Original vorher automatisch.
            </p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Empfehlung: Vorher einmal ein Voll-Backup ziehen (Admin → Backup → Herunterladen).
              Mindbody bleibt unberührt (keine neuen Mindbody-Termine).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlexConfirmOpen(false)}>Abbrechen</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={startFlexExecute}
              data-testid="flex-confirm-execute"
            >
              Jetzt ausführen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudioMigrationPage;
